import React, { createContext, useContext, useState } from "react";
import { ERROR, LOADING, SUCCESS, received, sent } from "../constants/common";
import { ReactComponent as WarningIcon } from "../assets/Icons/warning.svg";
import { useAlert } from "./Alert";
import {
  acceptReferral,
  getArchivedReferrals,
  getReferrals,
  getReferralsCount,
  getSentReferrals,
  markAsRead,
  rejectReferral,
  sendReferral,
} from "../services/referral.services";
import { suppressAlertPaths } from "../constants/lists";
import { useLocation } from "react-router-dom";

const ReferralsContext = createContext({});

export const ReferralsContextProvider = ({ children }) => {
  const { sendAlert } = useAlert();

  const [referralsState, setReferralsState] = useState({
    referrals: {
      data: [],
    },
    getReferralCountStatus: "",
    count: {
      received_count: 0,
      sent_count: 0,
    },
    fetchStatus: "",
    sendReferralStatus: "",
    acceptReferralStatus: "",
    rejectReferralStatus: "",
    markReferralStatus: "",
  });
  const location = useLocation();

  function handleState(obj) {
    setReferralsState((prevState) => ({
      ...prevState,
      ...obj,
    }));
  }

  async function fetchReferrals(params = {}, type) {
    handleState({ fetchStatus: LOADING });
    try {
      let response;
      if (!type || type === received) response = await getReferrals(params);
      if (type === sent) response = await getSentReferrals(params);

      handleState({
        referrals: { data: response.data.data },
        fetchStatus: SUCCESS,
      });
    } catch (error) {
      handleState({ fetchStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function fetchReferralsCount(data) {
    handleState({ getReferralCountStatus: LOADING });
    try {
      const response = await getReferralsCount(data);
      handleState({
        getReferralCountStatus: SUCCESS,
        count: response.data.data,
      });
    } catch (error) {
      handleState({ getReferralCountStatus: ERROR });
      const showAlert = !suppressAlertPaths.some((path) =>
        location.pathname.startsWith(path)
      );
      if (showAlert) {
        sendAlert(error.response?.data?.message, "error");
      }
      Promise.reject(error);
    }
  }

  async function sendReferal(data) {
    handleState({ sendReferralStatus: LOADING });
    try {
      const response = await sendReferral(data);
      if (response.data?.type === "warning") {
        sendAlert(response.data?.data?.message, "warning", true, null, WarningIcon);
        handleState({ sendReferralStatus: ERROR });
        return;
      }
      handleState({
        sendReferralStatus: SUCCESS,
      });
      sendAlert("Your referral has been sent.\nWe’ve notified the provider. If they respond, you’ll receive an update on TTN or by email.", "success");
    } catch (error) {
      handleState({ sendReferralStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  }

  async function acceptReferal(data) {
    handleState({ acceptReferralStatus: LOADING });
    try {
      await acceptReferral(data);
      handleState({
        acceptReferralStatus: SUCCESS,
      });
      sendAlert(
        "Thanks for accepting. We’ve let the referring provider know you’re available.",
        "success"
      );
    } catch (error) {
      handleState({ acceptReferralStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  }

  async function rejectReferal(data) {
    handleState({ rejectReferralStatus: LOADING });
    try {
      await rejectReferral(data);
      handleState({
        rejectReferralStatus: SUCCESS,
      });
      sendAlert(
        "Thanks for the update. We’ve let the referring provider know you’re unable to accept.",
        "success"
      );
    } catch (error) {
      handleState({ rejectReferralStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  }

  async function markReferralAsRead(referral_id) {
    handleState({ markReferralStatus: LOADING });
    try {
      await markAsRead({ referral_id });
      handleState({
        markReferralStatus: SUCCESS,
        referrals: {
          ...referralsState.referrals,
          data: referralsState.referrals.data.map((item) => {
            if (item.id === referral_id) {
              return {
                ...item,
                status: "seen",
              };
            }
            return item;
          }),
        },
      });
    } catch (error) {
      handleState({ markReferralStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  }

  function updateReferralsState(field, value) {
    handleState({ [field]: value });
  }

  const state = {
    ...referralsState,
    fetchReferrals,
    sendReferal,
    acceptReferal,
    rejectReferal,
    markReferralAsRead,
    fetchReferralsCount,
    updateReferralsState,
  };

  return (
    <ReferralsContext.Provider value={state}>
      {children}
    </ReferralsContext.Provider>
  );
};

export const useReferrals = () => useContext(ReferralsContext);
